import CustomButton from '@/components/atoms/CustomButton';
import { MAX_CONTAINER_WIDTH } from '@/constants/dimensions';
import { TUserMetadata } from '@/types/User.types';
import { Box, Button, HStack, Image, SimpleGrid, Text, VStack } from '@chakra-ui/react';
import UserContactDetailsBox from './components/UserContactDetailsBox';
import UserDemographicsCard from './components/UserDemographicsBox';
import UserProfilePhotoBox from './components/UserProfilePhotoBox';
import UserProfileBox from './components/UserProfileBox';
import roles from '@/constants/roles';
import iconsPng from '@/assets/img/png/icons';

interface UserProfileOverviewProps {
  title: string;
  updateProfilePhoto: (photo: File) => Promise<boolean>;
  userData: TUserMetadata;
  onClickEditContactDetails: () => void;
  onClickEditDemographics: () => void;
  onPhotoChange?: (photo: File) => void;
  onClickProceed?: () => void;
  onClickEditPeerSupportBio?: () => void;
  isLoading?: boolean;
  initialPreview?: string;
  hideEditContactButton?: boolean;
  hideEditDemographicsButton?: boolean;
  userRoles?: string[];
  showSkippedDemographics?: boolean;
}

const UserProfileOverview = ({
  title,
  updateProfilePhoto,
  userData,
  onClickEditContactDetails,
  onClickEditDemographics,
  onClickProceed,
  onClickEditPeerSupportBio,
  isLoading,
  initialPreview: initialPreviewUrl,
  hideEditContactButton = false,
  hideEditDemographicsButton = false,
  userRoles,
  showSkippedDemographics = false,
}: UserProfileOverviewProps) => {
  const isPeerSupport = userRoles?.includes(roles.peerSupport);

  return (
    <VStack width="100%" maxWidth={MAX_CONTAINER_WIDTH}>
      <Box
        position="absolute"
        backgroundImage={'/img/png/signup-top-right-background.png'}
        backgroundSize="cover"
        width="250px"
        height="30vh"
        right={0}
        top={0}
      ></Box>
      <Text variant={'loraTitle'} fontSize={'26px'} fontStyle={'italic'} color={'primary.veryDarkBlue'}>
        {title}
      </Text>

      <VStack width="100%" spacing="40px" marginTop="40px" marginBottom="70px" alignItems="stretch">
        <SimpleGrid columns={{ base: 1, lg: 2 }} spacing="40px" alignItems="stretch">
          <UserProfilePhotoBox updateProfilePhoto={updateProfilePhoto} initialPreviewUrl={initialPreviewUrl} />
          <UserContactDetailsBox
            email={userData?.email}
            firstName={userData?.firstName}
            lastName={userData?.lastName}
            phone={userData?.phone}
            location={userData.city ? `${userData.city}, ${userData.state}` : '/'}
            onClickEdit={onClickEditContactDetails}
            hideEditButton={hideEditContactButton}
          />
        </SimpleGrid>

        <SimpleGrid columns={{ base: 1, lg: 2 }} spacing="40px">
          <UserDemographicsCard
            showSkippedState={showSkippedDemographics}
            dateOfBirth={userData?.dateOfBirth}
            sex={userData?.sex}
            sexOther={userData?.sexOther}
            occupation={userData?.occupation}
            occupationOther={userData?.occupationOther}
            ethnicity={userData?.ethnicity}
            ethnicityOther={userData?.ethnicityOther}
            relationshipStatus={userData?.relationshipStatus}
            relationshipStatusOther={userData?.relationshipStatusOther}
            education={userData?.education}
            educationOther={userData?.educationOther}
            onClickEdit={onClickEditDemographics}
            hideEditButton={hideEditDemographicsButton}
          />
        </SimpleGrid>

        {isPeerSupport && onClickEditPeerSupportBio && (
          <SimpleGrid columns={{ base: 1, lg: 2 }} spacing="40px">
            <UserProfileBox style={{ minHeight: '270px', height: 'auto' }}>
              <HStack justifyContent="space-between">
                <Text variant="urbanistSemiBoldTitle" marginBottom="10px">Peer Support Biography</Text>
                {isPeerSupport && (
                  <Button
                    variant="link"
                    color="primary.500"
                    onClick={onClickEditPeerSupportBio}
                    leftIcon={<Image alt="" src={iconsPng.penPurple} width="12px" height="12px" />}
                  >
                    Edit
                  </Button>
                )}
              </HStack>
              <Text marginTop="20px" maxHeight="150px" overflowY="auto" variant="urbanistSemiBoldRegular" color="text.mediumBlue">
                {userData.bio || 'No peer support biography yet.'}
              </Text>
            </UserProfileBox>
          </SimpleGrid>
        )}
      </VStack>

      {onClickProceed && (
        <CustomButton
          backgroundColor="primary.teal"
          label="Proceed"
          width={'300px'}
          labelColor="white"
          onClick={onClickProceed}
          isLoading={isLoading}
        />
      )}
    </VStack>
  );
};

export default UserProfileOverview;
